import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

const FourLinerAlphaCapitalStencilsCanvas = ({ width = 350, height = 350, onClose }) => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const userId = authState.user._id
  const canvasRef = useRef(null);
  const [selectedLetter, setSelectedLetter] = useState('');
  const drawColor = 'black';
  const [isDrawing, setIsDrawing] = useState(false);

  const drawLinesAndLetters = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
  
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const groupHeight = 50; 
    const lineSpacing = 10;  
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''); 
  
    const drawLine = (y, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvasWidth, y);
      ctx.stroke();
    };

    const drawText = (text, x, y, color) => {
      ctx.fillStyle = color;
      ctx.font = '25px Arial'; 
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, x, y);
    };
  
    const lettersPerLine = 6;
    const letterSpacing = canvasWidth / lettersPerLine;
    let yOffset = 0; 
  
    for (let group = 0; group < Math.ceil(alphabet.length / lettersPerLine); group++) {
      drawLine(yOffset + 10, 'red');
      drawLine(yOffset + 20, 'lightblue');
      drawLine(yOffset + 30, 'lightblue');
      drawLine(yOffset + 40, 'red');
  
      const startIndex = group * lettersPerLine;
      const endIndex = Math.min(startIndex + lettersPerLine, alphabet.length);
  
      for (let i = startIndex; i < endIndex; i++) {
        const xPosition = (i - startIndex) * letterSpacing + letterSpacing / 2;
        const yPosition = yOffset + 21;
        ctx.strokeStyle = 'gray';
        ctx.lineWidth = 1;
        // ctx.strokeRect(xPosition - 15, yPosition - 20, 30, 30); // Draw the border box
        drawText(alphabet[i], xPosition, yPosition, 'lightgray'); // Draw the letter in light gray
      }
  
      yOffset += groupHeight + lineSpacing;
    }
  
    while (yOffset < canvasHeight) {
      drawLine(yOffset + 10, 'red');
      drawLine(yOffset + 20, 'lightblue');
      drawLine(yOffset + 30, 'lightblue');
      drawLine(yOffset + 40, 'red');
      yOffset += groupHeight + lineSpacing;
    }
  };

  useEffect(() => {
    drawLinesAndLetters();
  }, [width, height]);  


  // Handle mouse and touch events
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1.0;
    const startDrawing = (x, y) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      setIsDrawing(true);
    };

    const draw = (x, y) => {
      if (!isDrawing) return;
      ctx.lineTo(x, y);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath(); 
      }
    };

    // Mouse Events
    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;

    // Touch Events
    const handleTouchStart = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleTouchEnd = endDrawing;

    // Add event listeners for mouse and touch
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);

    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      // Remove event listeners for mouse and touch
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);

      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing]);

  useEffect(() => {
    if (selectedLetter) {
      drawTextOnCanvas(selectedLetter);
    }
  }, [selectedLetter]);

  const drawTextOnCanvas = (text) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    const boxWidth = 100;
    const boxHeight = 100;
    const boxX = (canvasWidth - boxWidth) / 2;
    const boxY = 20;  

    ctx.strokeStyle = 'gray';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

    // Draw four lines inside the box
    const lineSpacing = boxHeight / 5;
    const lineY1 = boxY + lineSpacing;
    const lineY2 = boxY + 2 * lineSpacing;
    const lineY3 = boxY + 3 * lineSpacing;
    const lineY4 = boxY + 4 * lineSpacing;

    // Draw lines
    ctx.strokeStyle = 'red';
    ctx.beginPath();
    ctx.moveTo(boxX, lineY1);
    ctx.lineTo(boxX + boxWidth, lineY1);
    ctx.stroke();

    ctx.strokeStyle = 'blue';
    ctx.beginPath();
    ctx.moveTo(boxX, lineY2);
    ctx.lineTo(boxX + boxWidth, lineY2);
    ctx.stroke();

    ctx.strokeStyle = 'blue';
    ctx.beginPath();
    ctx.moveTo(boxX, lineY3);
    ctx.lineTo(boxX + boxWidth, lineY3);
    ctx.stroke();

    ctx.strokeStyle = 'red';
    ctx.beginPath();
    ctx.moveTo(boxX, lineY4);
    ctx.lineTo(boxX + boxWidth, lineY4);
    ctx.stroke();

    ctx.lineWidth = 5;
    ctx.fillStyle = 'gray';
    ctx.font = '100px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvasWidth / 2, boxY+55);

    ctx.strokeStyle = 'gray';
    ctx.lineWidth = 1;
    ctx.strokeRect(boxX, boxY+110, boxWidth, boxHeight);

    ctx.fillStyle = 'gray';
    ctx.font = '100px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, canvasWidth / 2, boxY+160);

    ctx.strokeStyle = 'gray';
    ctx.lineWidth = 1;
    ctx.strokeRect(boxX, boxY+220, boxWidth, boxHeight);
};

useEffect(() => {
}, [width, height, selectedLetter]);

  const changeMode = (letter) => {
    setSelectedLetter(letter);
    drawTextOnCanvas(letter);  
};

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  return (
    <div style={{ marginTop: '50px', position: 'relative', backgroundColor: '#000000', opacity: 1 }}>
      <div className="mt-4 flex flex-wrap gap-2 justify-center" style={{ backgroundColor: '#000000', opacity: 1 }}>
        {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map((char, index) => (
          <div key={index} className="flex flex-row">
            <button 
              onClick={() => changeMode(char)} 
              className="p-4 bg-indigo-600 text-white text-xl rounded hover:bg-indigo-500 transition flex justify-center"
            >
              {char}
            </button>
          </div>
        ))}
      </div>
      <div className='flex flex-row' style={{ opacity: 1 }}>
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{
            border: '1px solid #ccc',
            backgroundColor: 'white',
            display: 'block',
            margin: '0 auto',
          }}
          className="canvas"
        />
      </div>
      <div className='flex flex-row'>
        <button onClick={clearCanvas}>Clear</button>
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
};

export default FourLinerAlphaCapitalStencilsCanvas;
